import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Sentry } from '@idk-web/core-utils';
import { useIsMounted } from '@idk-web/core-ui';
import { PrivateAppLoginStatusResponse } from '@idk-web/api';
import { isBankIDError, translateError } from '@/utils/error';
import {
  login as reduxLogin,
  logout as reduxLogout,
} from '@/redux/privateAuth.slice';
import { StartedPrivateLoginRequest } from '@/hooks/private/login/privateLogin';
import * as privateLogin from '@/hooks/private/login/privateLogin';

type State =
  | {
      state: 'IDLE';
    }
  | {
      state: 'CANCELLED';
    }
  | {
      state: 'TIMED_OUT';
    }
  | {
      state: 'WAITING';
      bankId?: {
        autoStartToken: string;
        qrData: string;
      };
      freja?: {
        qrData: string;
      };
    }
  | {
      state: 'COMPLETE';
    }
  | {
      state: 'FAILED';
      error: string;
    };

export type PrivateLogin = {
  loginBankIdSe(): Promise<void>;
  loginFreja(): Promise<void>;
  loginBankIdNo(window: Window): Promise<void>;
  loginMitId(window: Window): Promise<void>;
  loginFtn(window: Window): Promise<void>;
  logout(): Promise<void>;
  cancel(): Promise<void>;
  reset(): void;
} & State;

export function usePrivateLogin(): PrivateLogin {
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const { t } = useTranslation();
  const request = useRef<StartedPrivateLoginRequest>();
  const [state, setState] = useState<State>({ state: 'IDLE' });
  const onInit = useCallback(
    async (
      data: Extract<PrivateAppLoginStatusResponse, { status: 'IN_PROGRESS' }>,
    ) => {
      onProgress(data);
    },
    [],
  );
  const onComplete = useCallback(
    async (
      result: Extract<PrivateAppLoginStatusResponse, { status: 'COMPLETED' }>,
    ) => {
      dispatch(reduxLogin(result));

      if (isMounted()) {
        setState({ state: 'COMPLETE' });
      }

      request.current = undefined;
    },
    [],
  );
  const onError = useCallback(
    async (error: unknown) => {
      if (isMounted()) {
        if (error === 'cancelled') {
          setState({ state: 'CANCELLED' });
        } else if (error === 'timed out') {
          setState({ state: 'TIMED_OUT' });
        } else {
          if (!isBankIDError(error)) {
            Sentry.reportError('Private login failed', { error });
          }

          setState({ state: 'FAILED', error: translateError(t, error) });
        }
      }

      request.current = undefined;
    },
    [t],
  );
  const onProgress = useCallback(
    async (
      data: Extract<PrivateAppLoginStatusResponse, { status: 'IN_PROGRESS' }>,
    ) => {
      setState((oldState) => {
        let newState: State;
        if (data.bankId) {
          newState = { state: 'WAITING', bankId: data.bankId };
        } else if (data.freja) {
          newState = { state: 'WAITING', freja: data.freja };
        } else {
          newState = { state: 'WAITING' };
        }

        if (JSON.stringify(oldState) === JSON.stringify(newState)) {
          return oldState;
        } else {
          return newState;
        }
      });
    },
    [],
  );
  const loginBankIdSe = useCallback(async (): Promise<void> => {
    setState({ state: 'WAITING' });

    request.current = privateLogin
      .bankIdSe()
      .onInit(onInit)
      .onComplete(onComplete)
      .onError(onError)
      .onProgress(onProgress)
      .start();

    return request.current.wait();
  }, [onInit, onComplete, onError, onProgress]);
  const loginFreja = useCallback(async (): Promise<void> => {
    setState({ state: 'WAITING' });

    request.current = privateLogin
      .freja()
      .onInit(onInit)
      .onComplete(onComplete)
      .onError(onError)
      .onProgress(onProgress)
      .start();

    return request.current.wait();
  }, [onInit, onComplete, onError, onProgress]);
  const loginBankIdNo = useCallback(
    async (window: Window): Promise<void> => {
      setState({ state: 'WAITING' });

      request.current = privateLogin
        .bankIdNo(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const loginMitId = useCallback(
    async (window: Window): Promise<void> => {
      setState({ state: 'WAITING' });

      request.current = privateLogin
        .mitId(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const loginFtn = useCallback(
    async (window: Window): Promise<void> => {
      setState({ state: 'WAITING' });

      request.current = privateLogin
        .ftn(window)
        .onInit(onInit)
        .onComplete(onComplete)
        .onError(onError)
        .onProgress(onProgress)
        .start();

      return request.current.wait();
    },
    [onInit, onComplete, onError, onProgress],
  );
  const logout = useCallback(async (): Promise<void> => {
    dispatch(reduxLogout());
    reset();
  }, []);
  const cancel = useCallback(async () => {
    if (isMounted()) {
      setState({ state: 'CANCELLED' });
    }

    return request.current?.cancel();
  }, []);
  const reset = useCallback(() => {
    if (isMounted()) {
      setState({ state: 'IDLE' });
    }

    request.current = undefined;
  }, []);

  return useMemo(
    () => ({
      loginBankIdSe,
      loginFreja,
      loginBankIdNo,
      loginMitId,
      loginFtn,
      logout,
      cancel,
      reset,
      ...state,
    }),
    [
      loginBankIdSe,
      loginBankIdNo,
      loginMitId,
      loginFtn,
      logout,
      cancel,
      reset,
      state,
    ],
  );
}
